import {
  ClockIcon,
  BoltIcon,
  ArrowsRightLeftIcon,
  MoonIcon,
  UsersIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import Tooltip from "@components/Tooltip";

// Renderers for the customer-analytics facts on the trade-accounts table.
//
// The analytics service publishes three separate things where there used to be one "payer type":
// whether the customer is trading at all (activity state), how they pay when they do (timing
// persona), and how late "late" is (a presentation-only band). They are rendered as separate pills
// because they answer different questions — a New customer has no payment habit to show, and a
// customer moving from Late to Very late has not changed persona.
//
// Each pill's tooltip is written about this customer from the figures the analytics service
// publishes beside the label. When those figures have not arrived — a brand-new customer, or the
// detail fact lagging the label — the pill falls back to a static sentence about the rule, so it
// never blanks out. Every vocabulary here is owned by the analytics service, so an unknown value
// still falls back to a neutral tone and a humanized label.

export const normaliseFactValue = (value) => (value || "").toString().replace(/[\s_]/g, "").toLowerCase();

// Fallback for unknown values: split PascalCase / snake_case into spaced words.
const humanize = (value) =>
  (value || "")
    .toString()
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

// ---- Formatters. Money is shown exactly, not rounded to "£9k": a credit controller reading a
// hover wants the real figure. Dates are parsed from their Y-M-D parts rather than through Date()
// so a timezone can never nudge "14 Aug" back to the 13th.
const round = (value) => Math.round(value);
const money = (value) =>
  value == null ? "—" : `£${Number(value).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const daysLabel = (count) => `${count} ${count === 1 ? "day" : "days"}`;
const plural = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;
const signed = (value) => `${value > 0 ? "+" : ""}${value}`;
const oneDecimal = (value) => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const ymd = (value) => String(value).split("-").map(Number);
const shortDate = (value) => {
  if (!value) return null;
  const [, month, day] = ymd(value);
  return `${day} ${MONTHS_SHORT[month - 1]}`;
};
const monthName = (value) => {
  if (!value) return null;
  const [, month] = ymd(value);
  return MONTHS_LONG[month - 1];
};

// A day count read back as roughly the span a person would say out loud.
const relativeAge = (days) => {
  if (days == null) return null;
  if (days < 14) return plural(days, "day") + " ago";
  if (days < 60) return `${round(days / 7)} weeks ago`;
  if (days < 365) return `${round(days / 30)} months ago`;
  return `${round(days / 365)} years ago`;
};

const TONE = {
  emerald: "bg-emerald-50/25 border-emerald-200",
  amber: "bg-amber-50/25 border-amber-200",
  red: "bg-red-50/25 border-red-200",
  blue: "bg-blue-50/25 border-blue-200",
  zinc: "bg-zinc-50/25 border-zinc-200",
};
const ICON_TONE = {
  emerald: "text-emerald-600",
  amber: "text-amber-600",
  red: "text-red-600",
  blue: "text-blue-600",
  zinc: "text-zinc-500",
};
const PILL = "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap h-6";

// Nothing to show is shown as nothing to show. A dash rather than a zero, because "no overdue
// money" and "nought days late" are different statements about a customer.
const NOTHING_TO_SHOW = <span className="text-zinc-400">—</span>;

// Shown for the Payer type dash: no analytics facts exist for the account at all.
export const NO_PAYER_TYPE_DESCRIPTION = "No ledger activity yet – nothing to calculate.";

// ActivityState: New, Active, Dormant. `describe` writes the tooltip from this customer's figures;
// `description` is the fallback when they are absent.
export const ACTIVITY_STATE_META = {
  new: {
    label: "New",
    Icon: UsersIcon,
    tone: "blue",
    description: "First seen recently and not yet invoiced. No payment history yet.",
    describe: (d) =>
      d?.daysSinceFirstSeen != null
        ? `First seen ${relativeAge(d.daysSinceFirstSeen)}, no invoices yet – no payment history to read.`
        : null,
  },
  active: {
    label: "Active",
    Icon: CheckCircleIcon,
    tone: "emerald",
    description: "Invoiced recently but nothing settled yet, so there's no payment habit to read.",
    describe: (d) =>
      d?.invoicesInActivityWindow != null
        ? `${plural(d.invoicesInActivityWindow, "invoice")} in the last 4 months, none settled yet – no payment habit to read.`
        : null,
  },
  dormant: {
    label: "Dormant",
    Icon: MoonIcon,
    tone: "zinc",
    description: "No invoice activity in the last 4 months.",
    describe: (d) =>
      d?.daysSinceLastInvoiced != null
        ? `Last invoiced ${relativeAge(d.daysSinceLastInvoiced)} – nothing in the last 4 months.`
        : null,
  },
};

// TimingPersona: Early, OnTime, Late, Unpredictable. Late severity arrives separately as LateBand,
// so "Late" here is one persona however late the customer actually is.
const hasBaseline = (d) => d?.baselineSampleCount && d?.baselineP95DaysLate != null;
const lateBandDescribe = (d) =>
  hasBaseline(d)
    ? `Pays ${round(d.baselineP95DaysLate)} days late, 95% of the time (${plural(d.baselineSampleCount, "invoice")}, last 12 months).`
    : null;

export const TIMING_PERSONA_META = {
  early: {
    label: "Early",
    Icon: BoltIcon,
    tone: "emerald",
    description: "Reliably pays more than 3 days early.",
    describe: (d) =>
      hasBaseline(d)
        ? `Pays ${Math.abs(round(d.baselineP95DaysLate))} days early, 95% of the time (${plural(d.baselineSampleCount, "invoice")}, last 12 months).`
        : null,
  },
  ontime: {
    label: "On time",
    Icon: ClockIcon,
    tone: "emerald",
    description: "Reliably pays around the due date.",
    describe: (d) =>
      hasBaseline(d)
        ? `Pays within ${daysLabel(Math.max(1, Math.abs(round(d.baselineP95DaysLate))))} of due date, 95% of the time (${plural(d.baselineSampleCount, "invoice")}, last 12 months).`
        : null,
  },
  late: {
    label: "Late",
    Icon: ClockIcon,
    tone: "amber",
    description: "At their latest, more than a day past the due date.",
    describe: lateBandDescribe,
  },
  unpredictable: {
    label: "Unpredictable",
    Icon: ArrowsRightLeftIcon,
    tone: "red",
    description:
      "No consistent payment timing — there is a wide gap between their fastest and slowest payments.",
    describe: (d) =>
      d?.recentSampleCount && d?.recentSpreadDays != null
        ? `Payment timing swings by ${round(d.recentSpreadDays)} days between fastest and slowest (${plural(d.recentSampleCount, "invoice")}, last 3 months).`
        : null,
  },
};

// LateBand: presentation detail within Late. The label replaces the bare "Late" pill so the row
// reads as one verdict, and the tone escalates with the band.
export const LATE_BAND_META = {
  slightlylate: { label: "Slightly late", Icon: ClockIcon, tone: "amber", description: "Usually up to a week late, but still pays.", describe: lateBandDescribe },
  late: { label: "Late", Icon: ClockIcon, tone: "amber", description: "Usually one to two weeks late, but still pays.", describe: lateBandDescribe },
  verylate: { label: "Very late", Icon: ExclamationTriangleIcon, tone: "red", description: "Usually two to four weeks late, but still pays.", describe: lateBandDescribe },
  severelylate: { label: "Severely late", Icon: ExclamationTriangleIcon, tone: "red", description: "Usually over a month late, but still pays.", describe: lateBandDescribe },
};

// WatchFlagType as the analytics service publishes it. Names must match the enum member names or
// the chip falls back to a humanized label with no tone. Each `describe` reads the per-flag detail
// the analytics service publishes; `description` is the fallback when that detail is absent.
export const WATCH_FLAG_META = {
  disputeopen: {
    label: "Dispute open",
    tone: "zinc",
    description: "An unpaid invoice is under query; disputed invoices are excluded from all behaviour measures.",
    describe: (d) =>
      d?.invoiceAmount != null
        ? `${plural(d.invoiceCount ?? 1, "invoice")} under query (${d.invoiceReference ? `${d.invoiceReference}, ` : ""}${money(d.invoiceAmount)})`
        : null,
  },
  overcreditlimit: {
    label: "Over credit limit",
    tone: "red",
    description: "Open balance is above the customer's credit limit.",
    describe: (d) =>
      d?.openBalance != null && d?.creditLimit != null
        ? `Owes ${money(d.openBalance)} vs. ${money(d.creditLimit)} credit limit – over by ${money(d.openBalance - d.creditLimit)}.`
        : null,
  },
  nearcreditlimit: {
    label: "Near credit limit",
    tone: "amber",
    description: "Open balance is 70% or more of the customer's credit limit.",
    describe: (d) =>
      d?.openBalance != null && d?.creditLimit != null && d?.utilisationPercent != null
        ? `Owes ${money(d.openBalance)} vs. ${money(d.creditLimit)} credit limit – ${round(d.utilisationPercent)}% used.`
        : null,
  },
  rampingup: {
    label: "Ramping up",
    tone: "blue",
    description: "Invoiced volume in the last 30 days has at least doubled compared to their recent 90-day norm.",
    describe: (d) =>
      d?.recentValue != null && d?.baselineMonthlyValue != null && d?.recentAsMultipleOfBaseline != null
        ? `${money(d.recentValue)} invoiced in last 30 days (${plural(d.recentInvoiceCount ?? 0, "invoice")}) vs. ${money(d.baselineMonthlyValue)}/month over previous 90 days – ${oneDecimal(d.recentAsMultipleOfBaseline)}x the norm.`
        : null,
  },
  orderpause: {
    label: "Slowing down",
    tone: "blue",
    description: "Invoiced volume in the last 30 days has dropped to less than half of their recent 90-day norm.",
    describe: (d) =>
      d?.recentValue != null && d?.baselineMonthlyValue
        ? `${money(d.recentValue)} invoiced in last 30 days vs. ${money(d.baselineMonthlyValue)}/month over previous 90 days – down ${round((1 - d.recentValue / d.baselineMonthlyValue) * 100)}%.`
        : null,
  },
  laterthanusual: {
    label: "Later than usual",
    tone: "amber",
    description:
      "This customer's late invoices are now being paid later than the customer's usual payment behaviour.",
    describe: (d) =>
      d?.usualDaysLate != null && d?.currentDaysLate != null
        ? `Usually pays ${round(d.usualDaysLate)} days late, now ${round(d.currentDaysLate)} days late – ${money(d.overdueAmount)} overdue across ${plural(d.overdueInvoiceCount ?? 0, "invoice")}.`
        : null,
  },
  payingpartial: {
    label: "Paying partial",
    tone: "amber",
    description: "At least one overdue invoice has been recently part-paid.",
    describe: (d) => {
      if (d?.paidAmount == null || d?.outstandingAmount == null) return null;
      return d.invoiceReference
        ? `${money(d.paidAmount)} paid against a ${money(d.invoiceAmount)} overdue invoice (${d.invoiceReference}) – ${money(d.outstandingAmount)} still outstanding.`
        : `${money(d.paidAmount)} paid across ${plural(d.invoiceCount ?? 0, "overdue invoice")} – ${money(d.outstandingAmount)} still outstanding.`;
    },
  },
  new30dayspastdue: {
    label: "New 30d+ past due",
    tone: "red",
    description: "Recently had at least one invoice become 30 days overdue within the last 90 days.",
    describe: (d) => {
      if (!d?.crossedOn) return null;
      return d.invoiceReference
        ? `${d.invoiceReference} (${money(d.invoiceAmount)}) passed 30 days overdue on ${shortDate(d.crossedOn)} – first time in the last 90 days.`
        : `${plural(d.invoiceCount ?? 0, "invoice")} passed 30 days overdue by ${shortDate(d.crossedOn)} – first time in the last 90 days.`;
    },
  },
  atrisk: {
    label: "Payment behaviour deterioration",
    tone: "red",
    description:
      "This customer's payment behaviour is deteriorating noticeably faster than the rest of your customers.",
    describe: (d) =>
      d?.movementDays != null && d?.cohortMeanMovementDays != null
        ? `Paying ${round(d.movementDays)} days later than their 12-month norm (last 3 months) vs. ${signed(round(d.cohortMeanMovementDays))} days across your other customers.`
        : null,
  },
  overduejumped: {
    label: "Overdue jumped",
    tone: "red",
    description: "Overdue balance has at least doubled month-on-month.",
    describe: (d) => {
      if (d?.overdueNow == null || d?.overdueBefore == null) return null;
      // A jump from nothing has no multiple to quote — "£6,000 up from £0" reads on its own.
      const multiple = d.overdueBefore > 0 ? ` (${oneDecimal(d.overdueNow / d.overdueBefore)}x)` : "";
      return `${money(d.overdueNow)} overdue now vs. ${money(d.overdueBefore)} at end of ${monthName(d.comparedOn)} – up ${money(d.overdueNow - d.overdueBefore)}${multiple}.`;
    },
  },
};

const Pill = ({ meta, detail }) => {
  const { label, Icon, tone } = meta;
  const description = (meta.describe && meta.describe(detail)) || meta.description;
  return (
    <Tooltip content={description} wrapperClassName="inline-flex">
      <span className={`${PILL} ${TONE[tone]} text-zinc-700`}>
        {Icon && <Icon className={`h-4 w-4 ${ICON_TONE[tone]}`} strokeWidth={2} />}
        {label}
      </span>
    </Tooltip>
  );
};

const metaFor = (lookup, value) =>
  lookup[normaliseFactValue(value)] ?? { label: humanize(value), Icon: null, tone: "zinc" };

/**
 * What kind of payer this is, as one pill.
 *
 * The two facts behind it are never both meaningful: a timing persona is only claimed about a
 * customer who is trading, so a New or Dormant customer has no payment habit, and an Active one has
 * nothing to add beyond their habit. Whichever of the two says something is the one shown. The
 * `detail` carries the figures the shown pill's tooltip is written from.
 */
export const PayerTypeLabel = ({ activityState, timingPersona, lateBand, detail }) => {
  if (timingPersona) {
    const isLate = normaliseFactValue(timingPersona) === "late";
    const meta = isLate && lateBand
      ? metaFor(LATE_BAND_META, lateBand)
      : metaFor(TIMING_PERSONA_META, timingPersona);

    return <Pill meta={{ Icon: null, ...meta }} detail={detail} />;
  }

  if (activityState) {
    return <Pill meta={metaFor(ACTIVITY_STATE_META, activityState)} detail={detail} />;
  }

  // The dash here means something more specific than the one in any other column — no analytics
  // facts exist for this account at all — so it says so on hover rather than reading as a bug.
  return (
    <Tooltip content={NO_PAYER_TYPE_DESCRIPTION} wrapperClassName="inline-flex">
      {NOTHING_TO_SHOW}
    </Tooltip>
  );
};

// Least to most concerning, so a row reads calm-to-alarming left to right.
const TONE_SEVERITY = { emerald: 0, zinc: 1, blue: 2, amber: 3, red: 4 };

export const WatchFlagChips = ({ flags, details }) => {
  if (!flags?.length) return null;
  const detailFor = (flag) => details?.find((entry) => normaliseFactValue(entry.flag) === normaliseFactValue(flag));
  const ordered = flags
    .map((flag) => ({ flag, meta: metaFor(WATCH_FLAG_META, flag), detail: detailFor(flag) }))
    .sort((a, b) => (TONE_SEVERITY[b.meta.tone] ?? 1) - (TONE_SEVERITY[a.meta.tone] ?? 1));
  return (
    <div className="flex flex-wrap gap-1">
      {ordered.map(({ flag, meta, detail }) => (
        <Pill key={flag} meta={meta} detail={detail} />
      ))}
    </div>
  );
};

/**
 * How late the customer's oldest overdue invoice is.
 *
 * The worst single case rather than a weighted position across the ledger, which is the point of
 * showing it: a debt that has sat for months stays visible behind a large recent invoice that would
 * otherwise pull an average down. No trend arrow — the movement the analytics service publishes is
 * a movement in the weighted figure, and hanging it off this number would attribute a change in one
 * measure to another.
 */
export const DaysPastDueCell = ({ value }) => {
  // Absent, not zero. A customer with nothing overdue has no lateness to report, and the service
  // withholds the figure rather than calling it nought days late.
  if (value === null || value === undefined) return NOTHING_TO_SHOW;
  return <span>{Math.round(value)}d</span>;
};

// The four column headers describe the column, not a row, so they carry fixed, illustrative text —
// a header cannot quote figures for a customer it is not sitting on. The per-customer numbers live
// on the chip and cell hovers instead.

export const PAYER_TYPE_TOOLTIP = "The customer's payment pattern over the last 12 months";

export const DAYS_PAST_DUE_TOOLTIP = "How many days past due the account's oldest invoice is";

export const WATCH_FLAGS_TOOLTIP = "Important changes to the customer's payment pattern";

export const PAYMENT_SCORE_TOOLTIP =
  "A score that defines the customer's ability to pay on time. The higher the score (out of 100) " +
  "the more likely the customer will be a good payer, paying on-time and in-full.";

const rankSummary = (score) => {
  if (score >= 80) return "in the best-ranked fifth of";
  if (score >= 60) return "above the middle of";
  if (score > 40) return "around the middle of";
  if (score > 20) return "below the middle of";
  return "in the worst-ranked fifth of";
};

const percentileLine = (label, percentile) =>
  percentile === null || percentile === undefined
    ? null
    : `${label}: better than ${Math.round(percentile)}% of them`;

/**
 * Where the customer ranks against the merchant's other customers, 0–100, worst first by default.
 *
 * Two things about this number make a bare figure misleading, so neither is left to the reader to
 * guess. It is relative, so it moves when the peer group moves rather than only when this customer
 * does. And it is shrunk toward 50 in proportion to how little evidence there is, which means a
 * low-confidence score is pinned near the middle by arithmetic and carries almost no information
 * while looking every bit as authoritative as a well-evidenced one. That case shows the same
 * em-dash as any other absent verdict instead — the score is withheld, not rounded to "average".
 */
export const PerformanceScoreCell = ({ value, detail }) => {
  if (value === null || value === undefined) return NOTHING_TO_SHOW;

  const confidence = normaliseFactValue(detail?.confidence);
  if (confidence === "low") {
    return (
      <Tooltip content="Not enough invoices to rank this customer against your others yet.">
        <span className="text-zinc-400">—</span>
      </Tooltip>
    );
  }

  const score = Math.round(value);
  const tone = score >= 67 ? "text-emerald-700" : score >= 34 ? "text-amber-700" : "text-red-700";
  const peers = detail?.peerCount;
  const invoices = detail?.effectiveInvoiceCount;

  const lines = [
    peers ? `${score} — ${rankSummary(score)} your ${peers} ranked customers` : `${score} out of 100`,
    percentileLine("Overdue lateness", detail?.daysPastDuePercentile),
    invoices ? `Based on about ${Math.round(invoices)} invoices` : null,
    confidence === "medium" ? "Confidence: medium — treat as indicative" : null,
  ].filter(Boolean);

  return (
    <Tooltip content={lines.join(". ")}>
      <span
        className={`font-semibold ${tone} ${
          confidence === "medium" ? "decoration-dotted underline-offset-4 underline" : ""
        }`}
      >
        {score}
      </span>
    </Tooltip>
  );
};

// Filter option lists, so the dropdowns offer exactly the vocabulary the analytics service can
// return. The value sent to the API is the name the service publishes; the label is what the pills
// already show, derived from the same maps so the two cannot drift apart.
const optionsFrom = (meta, wireValues) =>
  wireValues.map((value) => ({ value, label: metaFor(meta, value).label }));

export const ACTIVITY_STATE_OPTIONS = optionsFrom(ACTIVITY_STATE_META, ["New", "Active", "Dormant"]);

export const TIMING_PERSONA_OPTIONS = optionsFrom(TIMING_PERSONA_META, [
  "Early",
  "OnTime",
  "Late",
  "Unpredictable",
]);

/**
 * Exactly what the Payer type column can display, in one list, drawn from the same presentation
 * maps that render the pills — so an option can never offer a value the column cannot show, or
 * omit one it can.
 *
 * A bare "Late" is absent on purpose: a Late customer always carries a severity band, and the band
 * is what the pill says, so "Late" here is the 8-to-14-day band rather than the persona. The
 * activity states come last because they are what is shown when there is no payment habit to show.
 */
export const PAYER_TYPE_OPTIONS = [
  ...optionsFrom(TIMING_PERSONA_META, ["Early", "OnTime"]),
  ...optionsFrom(LATE_BAND_META, ["SlightlyLate", "Late", "VeryLate", "SeverelyLate"]),
  ...optionsFrom(TIMING_PERSONA_META, ["Unpredictable"]),
  ...optionsFrom(ACTIVITY_STATE_META, ["New", "Active", "Dormant"]),
];

export const WATCH_FLAG_OPTIONS = optionsFrom(WATCH_FLAG_META, [
  "DisputeOpen",
  "OverCreditLimit",
  "NearCreditLimit",
  "RampingUp",
  "OrderPause",
  "LaterThanUsual",
  "PayingPartial",
  "New30DaysPastDue",
  "OverdueJumped",
  "AtRisk",
]);
